// Library data (GET /library) and the pure logic the page needs: the folder tree, what a folder contains,
// filtering and sorting, and what a delete would remove.

/** A route, a track or a point of interest on its own. */
export type ItemKind = 'route' | 'track' | 'poi';

/** The translation key for a kind's name ("Route", "Track", "POI"). */
export const kindLabelKey = (kind: ItemKind) =>
  kind === 'route' ? 'library.route' : kind === 'track' ? 'library.track' : 'library.poi';

export interface LibraryFolder {
  guid: string;
  name: string;
  /** null = top level ("My Stuff"). */
  parentGuid: string | null;
}

export interface LibraryItem {
  guid: string;
  kind: ItemKind;
  name: string;
  created: string;
  distance: number;
  totalAscent: number;
  onPhone: boolean;
  /** Empty = top level. An item can be in several folders. */
  folderGuids: string[];
}

export interface LibraryContents {
  folders: LibraryFolder[];
  items: LibraryItem[];
}

export interface Selection {
  items: { kind: ItemKind; guid: string }[];
  folders: string[];
}

/** Sub-folders of a folder (null = top level), by name. */
export function childFolders(folders: LibraryFolder[], parentGuid: string | null): LibraryFolder[] {
  return folders
    .filter((f) => f.parentGuid === parentGuid)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

/** The folder and every folder below it. */
export function subtree(folders: LibraryFolder[], guid: string): Set<string> {
  const result = new Set([guid]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (f.parentGuid && result.has(f.parentGuid) && !result.has(f.guid)) {
        result.add(f.guid);
        grew = true;
      }
    }
  }
  return result;
}

/** Folders from the top level down to this one (empty for the top level). */
export function pathTo(folders: LibraryFolder[], guid: string | null): LibraryFolder[] {
  const byGuid = new Map(folders.map((f) => [f.guid, f]));
  const path: LibraryFolder[] = [];
  let current = guid ? byGuid.get(guid) : undefined;
  while (current && !path.includes(current)) {
    path.unshift(current);
    current = current.parentGuid ? byGuid.get(current.parentGuid) : undefined;
  }
  return path;
}

/** Routes and tracks directly in a folder (null = items in no folder). */
export function itemsIn(items: LibraryItem[], folderGuid: string | null): LibraryItem[] {
  return items.filter((i) =>
    folderGuid === null ? i.folderGuids.length === 0 : i.folderGuids.includes(folderGuid),
  );
}

export type KindFilter = 'all' | ItemKind;
export type SortOrder = 'newest' | 'oldest' | 'name';

export function filterAndSort(
  items: LibraryItem[],
  { kind, search, sort }: { kind: KindFilter; search: string; sort: SortOrder },
): LibraryItem[] {
  const query = search.trim().toLocaleLowerCase();
  const shown = items.filter(
    (i) => (kind === 'all' || i.kind === kind) && (!query || i.name.toLocaleLowerCase().includes(query)),
  );
  const byDate = (a: LibraryItem, b: LibraryItem) => a.created.localeCompare(b.created);
  return shown.sort(
    sort === 'name'
      ? (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })
      : sort === 'oldest'
        ? byDate
        : (a, b) => byDate(b, a),
  );
}

/** Items whose names appear more than once in the list (flagged "Possible duplicate" after the first). */
export function possibleDuplicates(items: LibraryItem[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const item of [...items].sort((a, b) => a.created.localeCompare(b.created))) {
    const key = `${item.kind}:${item.name.trim().toLocaleLowerCase()}`;
    if (seen.has(key)) duplicates.add(item.guid);
    seen.add(key);
  }
  return duplicates;
}

export interface DeleteSummary {
  /** Routes and tracks that will be deleted. */
  items: number;
  /** Folders that will be deleted, including sub-folders. */
  folders: number;
  /** Items inside the deleted folders that are kept because they are also in another folder. */
  kept: number;
}

/** What deleting a selection removes (mirrors the API: items filed elsewhere survive a folder delete). */
export function deleteSummary(contents: LibraryContents, selection: Selection): DeleteSummary {
  const doomedFolders = new Set<string>();
  for (const guid of selection.folders) for (const f of subtree(contents.folders, guid)) doomedFolders.add(f);

  const doomedItems = new Set(selection.items.map((i) => i.guid));
  let kept = 0;
  for (const item of contents.items) {
    if (doomedItems.has(item.guid) || !item.folderGuids.some((f) => doomedFolders.has(f))) continue;
    if (item.folderGuids.every((f) => doomedFolders.has(f))) doomedItems.add(item.guid);
    else kept++;
  }
  return { items: doomedItems.size, folders: doomedFolders.size, kept };
}

/** Folders a selection can't be moved into: the selected folders themselves and anything below them. */
export function blockedTargets(folders: LibraryFolder[], selectedFolders: string[]): Set<string> {
  const blocked = new Set<string>();
  for (const guid of selectedFolders) for (const f of subtree(folders, guid)) blocked.add(f);
  return blocked;
}
